import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import { Icon } from "../../icons";
import CheckIcon from "@mui/icons-material/Check";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import {
  Drawer,
  TextField,
  Tooltip,
  Paper,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Divider,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-hot-toast";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
/* ---------------- COLORS (Local override for preview as per user code) ---------------- */
// const COLORS = {
//   PRIMARY: "#5E2E91",
//   LIGHT_LINE: "#E6DAF3",
//   BORDER: "#ECE7F2",
//   TEXT: "#421A6E"
// };

/* ---------------- DUMMY DATA ---------------- */
const projectData = {
  type: "Residential Project",
  name: "Arc Tower",
  developer: "Ram Realty",
  address: "Pune, Maharashtra",
  contact: "+918530844548",

  components: [
    {
      title: "Parking",
      details: "Details added about parking",
      children: [
        {
          title: "Basement Parking",
          details: "Details added about Basement Parking",
          children: [
            {
              title: "B1",
              details: "Details added about B1",
            },
            {
              title: "B2",
              details: "Details added about B2",
            },
          ],
        },
        {
          title: "Podium Parking",
          details: "Details added about Podium Parking",
        },
      ],
    },
    {
      title: "Floors",
      details: "Details about Floor like total Floors and all",
    },
  ],
};

/* ---------------- CARD ---------------- */
const InfoCard = ({ title, details, extraDetails }) => (
  <Box
    sx={{
      border: `1px solid ${COLORS.BORDER}`,
      borderRadius: "8px",
      backgroundColor: "#fff",
      padding: "14px 18px",
      position: "relative",
    }}
  >
    <Typography
      sx={{
        fontSize: 15,
        fontWeight: 600,
        color: COLORS.TEXT,
        fontFamily: FONT_FAMILY,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: 13,
        color: COLORS.TEXT,
        mt: 0.5,
        fontFamily: FONT_FAMILY,
      }}
    >
      {details}
    </Typography>
    {extraDetails && Array.isArray(extraDetails) && extraDetails.length > 0 && (
      <Box sx={{ mt: 1 }}>
        {extraDetails.map((detail, idx) => (
          <Typography
            key={idx}
            sx={{
              fontSize: "12px",
              color: COLORS.TEXT,
              opacity: 0.8,
              fontFamily: FONT_FAMILY,
            }}
          >
            <b>{detail.key}:</b> {detail.value}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
);

/* ---------------- TREE NODE ---------------- */
const TreeNode = ({
  node,
  isTopLevel,
  isFirst,
  isLast,
  isMaster,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  onPaste,
  hasCopiedNode,
  masterNode,
  onDetailChange,
  onAddSibling,
  onAddChild,
  onRemoveDetail,
  masterMapping,
  allMasterMappings,
  expandedNodes,
  toggleNode,
  onInlineRename,
  onMoveUp,
  onMoveDown,
  actionNodeId,
  canEdit = true,
}) => {
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [inlineTitle, setInlineTitle] = useState(node.title);

  const isExpanded = expandedNodes
    ? expandedNodes.has(node._id) || (isTopLevel && !expandedNodes.has(`collapsed_${node._id || node.title}`))
    : isTopLevel;
  const hasChildren =
    (node.children && node.children.length > 0) ||
    (node.subItems && node.subItems.length > 0);

  // For project mode, we prefer using the keys saved in the node itself
  const detailKeys = isMaster
    ? (node.extraDetails || []).map((d) => (typeof d === "string" ? d : d.key))
    : (node.extraDetails && Array.isArray(node.extraDetails) && node.extraDetails.length > 0)
      ? node.extraDetails.map((d) => (typeof d === "string" ? d : d.key))
      : (masterNode?.extraDetails || []).map((d) => (typeof d === "string" ? d : d.key));

  const childOptions = masterNode?.subItems || [];

  // Find siblings in master templates
  const getSiblingOptions = () => {
    if (!allMasterMappings || !masterNode) return [];

    // Search in all templates
    let siblings = [];
    const findInTemplates = (templates) => {
      for (const template of templates) {
        // Check top level
        if (template.components.some((c) => c._id === masterNode._id)) {
          siblings = [...siblings, ...template.components];
          continue;
        }
        // Check nested
        const findParent = (items) => {
          for (const item of items) {
            if (item.subItems?.some((s) => s._id === masterNode._id)) {
              siblings = [...siblings, ...item.subItems];
              return;
            }
            if (item.subItems) findParent(item.subItems);
          }
        };
        findParent(template.components);
      }
    };
    findInTemplates(allMasterMappings);
    return siblings;
  };

  const siblingOptions = getSiblingOptions();

  return (
    <Box sx={{ mb: 2, position: "relative" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        {hasChildren && (
          <IconButton
            size="small"
            onClick={() => toggleNode(node._id, isTopLevel)}
            sx={{ color: COLORS.PRIMARY, p: 0.5, mt: 0.5 }}
          >
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        )}
        {!hasChildren && <Box sx={{ width: 32 }} />}

        <Box sx={{ flex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: `1px solid ${COLORS.BORDER || "#ECE7F2"}`,
              borderRadius: "8px",
              backgroundColor: "#fff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              {isMaster && isInlineEditing ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, mr: 1 }}>
                  <TextField
                    autoFocus
                    size="small"
                    value={inlineTitle}
                    onChange={(e) => setInlineTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onInlineRename(node, inlineTitle.trim());
                        setIsInlineEditing(false);
                      } else if (e.key === "Escape") {
                        setInlineTitle(node.title);
                        setIsInlineEditing(false);
                      }
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        fontSize: "15px",
                        fontWeight: 700,
                        borderRadius: "6px",
                        "&.Mui-focused fieldset": { borderColor: COLORS.PRIMARY },
                      },
                    }}
                  />
                  {actionNodeId === node._id ? (
                    <CircularProgress size={16} sx={{ color: COLORS.PRIMARY, mx: 1 }} />
                  ) : (
                    <Tooltip title="Save (Enter)">
                      <IconButton
                        size="small"
                        sx={{ color: "success.main" }}
                        onClick={() => {
                          onInlineRename(node, inlineTitle.trim());
                          setIsInlineEditing(false);
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Cancel (Esc)">
                    <IconButton
                      size="small"
                      sx={{ color: "grey.500" }}
                      onClick={() => {
                        setInlineTitle(node.title);
                        setIsInlineEditing(false);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      fontWeight: 700,
                      color: COLORS.SECONDARY,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {node.title}
                  </Typography>
                  {actionNodeId === node._id ? (
                    <CircularProgress size={16} sx={{ color: COLORS.PRIMARY, ml: 1 }} />
                  ) : (
                    isMaster && canEdit && (
                      <Tooltip title="Quick rename title">
                        <IconButton
                          size="small"
                          sx={{
                            color: COLORS.PRIMARY,
                            opacity: 0.4,
                            p: 0.3,
                            "&:hover": { opacity: 1 },
                          }}
                          onClick={() => {
                            setInlineTitle(node.title);
                            setIsInlineEditing(true);
                          }}
                        >
                          <EditIcon sx={{ fontSize: "13px" }} />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </Box>
              )}
              {!isMaster && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(node)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {!isMaster && detailKeys.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
                {detailKeys.map((key, kIdx) => (
                  <Box key={kIdx} sx={{ minWidth: "150px", flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: COLORS.PRIMARY,
                          fontWeight: 600,
                        }}
                      >
                        {key}
                      </Typography>
                      {node.extraDetails && node.extraDetails[kIdx] && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            onRemoveDetail(node._id, kIdx, node.extraDetails)
                          }
                          sx={{
                            p: 0,
                            color: "error.main",
                            opacity: 0.5,
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: "12px" }} />
                        </IconButton>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`Enter ${key}...`}
                      value={
                        node.extraDetails && Array.isArray(node.extraDetails)
                          ? node.extraDetails[kIdx]?.value || ""
                          : ""
                      }
                      onChange={(e) =>
                        onDetailChange(node._id, kIdx, e.target.value)
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "4px",
                          fontSize: "13px",
                          backgroundColor: "#F9F9FB",
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {(isMaster || detailKeys.length === 0) && (
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.GREY_COLOR,
                  mt: 0.5,
                }}
              >
                {isMaster
                  ? node.extraDetails
                      ?.map((d) => (typeof d === "string" ? d : d.key))
                      .join(", ") || "No extra keys defined"
                  : node.extraDetails
                      ?.map((d) => (typeof d === "string" ? d : d.key))
                      .join(", ") || ""}
              </Typography>
            )}

            {!isMaster && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: "160px" }}>
                    <Select
                      displayEmpty
                      value=""
                      onChange={(e) => {
                        if (e.target.value)
                          onAddSibling(node._id, e.target.value);
                      }}
                      renderValue={() => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <AddIcon sx={{ fontSize: "14px" }} />
                          <Typography
                            sx={{ fontSize: "11px", fontWeight: 700 }}
                          >
                            Add Same Level
                          </Typography>
                        </Box>
                      )}
                      sx={{ height: "28px", borderRadius: "4px" }}
                    >
                      {siblingOptions.map((opt) => (
                        <MenuItem key={opt._id} value={opt}>
                          {opt.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: "160px" }}>
                    <Select
                      displayEmpty
                      value=""
                      disabled={childOptions.length === 0}
                      onChange={(e) => {
                        if (e.target.value)
                          onAddChild(node._id, e.target.value);
                      }}
                      renderValue={() => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <SubdirectoryArrowRightIcon
                            sx={{ fontSize: "14px" }}
                          />
                          <Typography
                            sx={{ fontSize: "11px", fontWeight: 700 }}
                          >
                            Add Sub Level
                          </Typography>
                        </Box>
                      )}
                      sx={{ height: "28px", borderRadius: "4px" }}
                    >
                      {childOptions.map((opt) => (
                        <MenuItem key={opt._id} value={opt}>
                          {opt.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
          </Paper>
        </Box>

        {isMaster && canEdit && (
          <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
            {hasCopiedNode && (
              <Tooltip title="Apply / Merge Component">
                <IconButton
                  size="small"
                  onClick={() => onPaste(node)}
                  sx={{ color: COLORS.SECONDARY }}
                >
                  <ContentPasteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Move Up">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onMoveUp(node)}
                  sx={{ color: COLORS.SECONDARY }}
                  disabled={isFirst}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move Down">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onMoveDown(node)}
                  sx={{ color: COLORS.SECONDARY }}
                  disabled={isLast}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Copy Item">
              <IconButton
                size="small"
                onClick={() => onCopy(node)}
                sx={{ color: COLORS.PRIMARY }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Sub-item">
              <IconButton
                size="small"
                onClick={() => onAdd(node)}
                sx={{ color: COLORS.PRIMARY }}
              >
                <AddCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => onEdit(node)}
                sx={{ color: COLORS.SECONDARY }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => onDelete(node)}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {hasChildren && isExpanded && (
        <Box
          sx={{
            ml: 2,
            pl: 3,
            borderLeft: `2px solid ${COLORS.LIGHT_SECONDARY}`,
            mt: 0.5,
            pb: 0.5,
          }}
        >
          {(node.children || node.subItems || []).map((child, idx, arr) => {
            const mChild = masterNode?.subItems?.find(
              (m) => m.title === child.title,
            );
            return (
              <Box key={child._id || idx} sx={{ mt: 2 }}>
                <TreeNode
                  node={child}
                  isFirst={idx === 0}
                  isLast={idx === arr.length - 1}
                  isMaster={isMaster}
                  onAdd={onAdd}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onCopy={onCopy}
                  onPaste={onPaste}
                  hasCopiedNode={hasCopiedNode}
                  masterNode={mChild}
                  onDetailChange={onDetailChange}
                  onAddSibling={onAddSibling}
                  onAddChild={onAddChild}
                  onRemoveDetail={onRemoveDetail}
                  masterMapping={masterMapping}
                  allMasterMappings={allMasterMappings}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                  onInlineRename={onInlineRename}
                  onMoveUp={onMoveUp}
                  onMoveDown={onMoveDown}
                  actionNodeId={actionNodeId}
                  canEdit={canEdit}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */
const ProjectMappingPreview = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, hasRole } = useAuth?.() || {};

  const location = useLocation();
  const isFirMasterMode = location.pathname.includes("/admin/fir-mappings/");

  const isMasterMode =
    isFirMasterMode ||
    [
      "residential",
      "commercial",
      "industrial",
      "mixed-use",
      "hospitality",
    ].includes(projectId?.toLowerCase()) ||
    (projectId && !/^[0-9a-fA-F]{24}$/.test(projectId));

  const [project, setProject] = useState(null);
  const [masterMapping, setMasterMapping] = useState(null);
  const [allMasterMappings, setAllMasterMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionNodeId, setActionNodeId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    node: null,
    type: null, // 'master' or 'project'
  });
  const [showDeleteTemplate, setShowDeleteTemplate] = useState(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [targetNode, setTargetNode] = useState(null);
  const [nodeTitle, setNodeTitle] = useState("");
  const [nodeDetails, setNodeDetails] = useState("");
  const [extraDetailsList, setExtraDetailsList] = useState([
    { key: "", value: "" },
  ]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const scrollPosRef = React.useRef({ windowY: 0, containerY: 0 });
  const scrollContainerRef = React.useRef(null);

  const saveScrollPosition = () => {
    scrollPosRef.current = {
      windowY: window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0,
      containerY: scrollContainerRef.current ? scrollContainerRef.current.scrollTop : 0,
    };
  };

  const restoreScrollPosition = () => {
    const { windowY, containerY } = scrollPosRef.current;
    const restore = () => {
      if (scrollContainerRef.current && containerY > 0) {
        scrollContainerRef.current.scrollTop = containerY;
      }
      if (windowY > 0) {
        window.scrollTo({ top: windowY, behavior: "instant" });
      }
    };
    restore();
    requestAnimationFrame(restore);
    setTimeout(restore, 50);
    setTimeout(restore, 150);
  };

  const toggleNode = (nodeId, isTopLevel = false) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (isTopLevel) {
        const collapseKey = `collapsed_${nodeId}`;
        if (next.has(collapseKey)) {
          next.delete(collapseKey);
        } else {
          next.add(collapseKey);
          next.delete(nodeId);
        }
      } else {
        if (next.has(nodeId)) next.delete(nodeId);
        else next.add(nodeId);
      }
      return next;
    });
  };
  const [copiedNode, setCopiedNode] = useState(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const lastProjectIdRef = React.useRef(projectId);

  const DRAFT_KEY = `mapping_screen_draft_${projectId || "new"}`;

  // Save to draft whenever important state changes
  useEffect(() => {
    // If projectId changed, we need to wait for the reset/load cycle to finish
    // before we start saving state into the NEW DRAFT_KEY.
    if (lastProjectIdRef.current !== projectId) {
      lastProjectIdRef.current = projectId;
      return;
    }

    if (loading) return; // Don't save while loading initial data

    const draftData = {
      nodeTitle,
      nodeDetails,
      extraDetailsList,
      modalOpen,
      modalMode,
      targetNode,
      timestamp: new Date().getTime(),
    };

    // If in project mode, also save the components we've built
    if (!isMasterMode && project) {
      draftData.projectComponents = project.components;
    }

    localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
  }, [
    nodeTitle,
    nodeDetails,
    extraDetailsList,
    modalOpen,
    modalMode,
    targetNode,
    project,
    isMasterMode,
    DRAFT_KEY,
    loading
  ]);

  // Helper to recursively strip IDs and other metadata
  const stripMetadata = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(stripMetadata);
    } else if (obj !== null && typeof obj === "object") {
      const newObj = {};
      for (const key in obj) {
        if (["_id", "__v", "createdAt", "updatedAt"].includes(key)) continue;
        newObj[key] = stripMetadata(obj[key]);
      }
      return newObj;
    }
    return obj;
  };

  const handleCopy = (node) => {
    const cleanNode = stripMetadata(node);
    setCopiedNode(cleanNode);
    toast.success(`${node.title} copied to clipboard`);
  };

  // Generate a unique title among siblings by appending a numeric counter
  const getUniquePasteTitle = (baseTitle, siblings) => {
    const existingTitles = new Set(siblings.map((s) => s.title));
    if (!existingTitles.has(baseTitle)) return baseTitle;
    let counter = 1;
    while (existingTitles.has(`${baseTitle} ${counter}`)) {
      counter++;
    }
    return `${baseTitle} ${counter}`;
  };

  const handlePaste = async (parent = null) => {
    if (!copiedNode) return;

    try {
      setSaving(true);

      if (parent) {
        // Merge the copied node into the target parent
        const mergedExtraDetails = [
          ...(parent.extraDetails || []),
          ...(copiedNode.extraDetails || []),
        ].reduce((acc, current) => {
          const key1 = typeof current === 'string' ? current : current.key;
          const exists = acc.some(item => (typeof item === 'string' ? item : item.key) === key1);
          if (!exists) return acc.concat([current]);
          return acc;
        }, []);

        // 1. Update parent's extraDetails
        await axios.put(
          `${url}/api/master-mappings/update-item/${masterMapping._id}`,
          {
            itemId: parent._id,
            updateData: {
              extraDetails: mergedExtraDetails,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // 2. Add each sub-item as a child of parent
        const cleanCopiedSubItems = stripMetadata(copiedNode.subItems || copiedNode.children || []);
        
        for (const subItem of cleanCopiedSubItems) {
           await axios.post(
             `${url}/api/master-mappings/add-item/${masterMapping._id}`,
             {
               parentId: parent._id,
               itemData: subItem,
             },
             { headers: { Authorization: `Bearer ${token}` } }
           );
        }

        toast.success(`Data applied to "${parent.title}"`);
      } else {
        // Determine sibling list to compute a unique title for main component
        const siblings = masterMapping?.components || [];
        const uniqueTitle = getUniquePasteTitle(copiedNode.title, siblings);
        const itemData = { ...copiedNode, title: uniqueTitle };

        await axios.post(
          `${url}/api/master-mappings/add-item/${masterMapping._id}`,
          {
            parentId: masterMapping._id,
            itemData,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success(`Item cloned as "${uniqueTitle}"`);
      }
      
      setCopiedNode(null);
      fetchMasterMapping(true);
    } catch (error) {
      console.error("Error cloning item:", error);
      toast.error("Failed to clone item");
    } finally {
      setSaving(false);
    }
  };

  const handleMoveNode = async (node, direction) => {
    saveScrollPosition();
    try {
      setActionNodeId(node._id);
      await axios.put(
        `${url}/api/master-mappings/reorder-item/${masterMapping._id}`,
        {
          itemId: node._id,
          direction,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`Node moved ${direction} successfully!`);
      await fetchMasterMapping(true);
    } catch (error) {
      console.error("Error moving node:", error);
      toast.error(error.response?.data?.message || "Failed to move node");
    } finally {
      setActionNodeId(null);
      restoreScrollPosition();
    }
  };

  // Inline rename handler — sends a quick update to the API
  const handleInlineRename = async (node, newTitle) => {
    if (!newTitle || newTitle === node.title) return;
    saveScrollPosition();

    // Optimistically update local state so title updates in place immediately
    setMasterMapping((prev) => {
      if (!prev || !prev.components) return prev;
      const updateTitleRecursive = (items) => {
        return items.map((item) => {
          if (item._id === node._id) {
            return { ...item, title: newTitle };
          }
          if (item.subItems) {
            return { ...item, subItems: updateTitleRecursive(item.subItems) };
          }
          if (item.children) {
            return { ...item, children: updateTitleRecursive(item.children) };
          }
          return item;
        });
      };
      return { ...prev, components: updateTitleRecursive(prev.components) };
    });

    try {
      setActionNodeId(node._id);
      await axios.put(
        `${url}/api/master-mappings/update-item/${masterMapping._id}`,
        {
          itemId: node._id,
          updateData: {
            title: newTitle,
            extraDetails: node.extraDetails || [],
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Renamed to "${newTitle}"`);
      await fetchMasterMapping(true);
    } catch (error) {
      console.error("Error renaming item:", error);
      toast.error("Failed to rename item");
    } finally {
      setActionNodeId(null);
      restoreScrollPosition();
    }
  };

  useEffect(() => {
    const comps = isMasterMode ? masterMapping?.components : project?.components;
    if (comps && Array.isArray(comps) && comps.length > 0) {
      setExpandedNodes((prev) => {
        let changed = false;
        const next = new Set(prev);
        comps.forEach((c) => {
          if (c._id && !next.has(c._id) && !next.has(`collapsed_${c._id}`)) {
            next.add(c._id);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [masterMapping?.components, project?.components, isMasterMode]);

  useEffect(() => {
    // Reset modal state on tab switch
    setModalOpen(false);
    setTargetNode(null);
    setNodeTitle("");
    setNodeDetails("");
    setExtraDetailsList([{ key: "", value: "" }]);
    setModalMode("add");

    if (projectId) {
      if (isMasterMode) {
        fetchMasterMapping().then(() => {
          loadDraft();
        });
      } else {
        fetchProjectDetails().then(() => {
          loadDraft();
        });
      }
    } else {
      setLoading(false);
      loadDraft();
    }
  }, [projectId, isMasterMode]);

  const loadDraft = () => {
    const savedDraft = localStorage.getItem(DRAFT_KEY);
    if (!savedDraft) return;

    try {
      const draft = JSON.parse(savedDraft);
      
      // Only restore if draft is relatively recent (e.g., last 24 hours) or user is returning
      // We'll be generous and just load it.
      
      if (draft.nodeTitle) setNodeTitle(draft.nodeTitle);
      if (draft.nodeDetails) setNodeDetails(draft.nodeDetails);
      if (draft.extraDetailsList) setExtraDetailsList(draft.extraDetailsList);
      if (draft.modalOpen) setModalOpen(draft.modalOpen);
      if (draft.modalMode) setModalMode(draft.modalMode);
      if (draft.targetNode) setTargetNode(draft.targetNode);

      if (!isMasterMode && draft.projectComponents) {
        setProject((prev) => {
          if (!prev) return prev;
          return { ...prev, components: draft.projectComponents };
        });
      }

      setIsDraftLoaded(true);
      // We don't toast here to avoid annoying the user every time they mount
    } catch (e) {
      console.error("Error loading draft:", e);
    }
  };

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const [pRes, mRes] = await Promise.all([
        axios.get(`${url}/api/projects/get/${projectId}`, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }),
        axios.get(`${url}/api/master-mappings/all`, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }),
      ]);

      if (pRes.data.success) {
        setProject(pRes.data.data);
      }

      const mapRes = await axios
        .get(`${url}/api/project-mappings/get/${projectId}`, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        })
        .catch(() => null);

      if (mapRes && mapRes.data.success && mapRes.data.data.length > 0) {
        const mapping = mapRes.data.data[0];
        setProject((prev) => ({
          ...prev,
          projectType: mapping.projectType,
          sourceTemplates: mapping.sourceTemplates,
          components: mapping.components,
        }));
      }

      if (mRes.data.success) {
        setAllMasterMappings(mRes.data.data);
        // Find "primary" master mapping by type for backwards compatibility
        const pType = pRes.data.data.projectType || pRes.data.data.type;
        const primary = mRes.data.data.find((m) => m.mappingName === pType);
        setMasterMapping(primary);
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterMapping = async (silent = false) => {
    if (!silent && !masterMapping) setLoading(true);
    try {
      const fetchMappingUrl = isFirMasterMode
        ? `${url}/api/fir/mappings/${projectId}`
        : `${url}/api/master-mappings/get-by-name/${projectId}`;

      const [mRes, allMRes] = await Promise.all([
        axios.get(fetchMappingUrl, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }),
        axios.get(`${url}/api/master-mappings/all`, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }),
      ]);

      if (mRes.data.success) {
        setMasterMapping(mRes.data.data);
      }
      if (allMRes.data.success) {
        setAllMasterMappings(allMRes.data.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Not found is okay, we'll suggest creating it
        setMasterMapping({ mappingName: projectId, components: [] });
      } else {
        console.error("Error fetching master mapping:", error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleAddNode = (parent = null) => {
    setModalMode("add");
    setTargetNode(parent);
    setNodeTitle("");
    setNodeDetails("");
    setExtraDetailsList([{ key: "", value: "" }]);
    setModalOpen(true);
  };

  const handleEditNode = (node) => {
    setModalMode("edit");
    setTargetNode(node);
    setNodeTitle(node.title);
    setNodeDetails(
      node.details || node.extraDetails?.map((d) => d.key).join(", ") || "",
    );
    setExtraDetailsList(
      node.extraDetails && Array.isArray(node.extraDetails)
        ? node.extraDetails.map((d) => ({
            key: d.key || "",
            value: d.value || "",
          }))
        : [{ key: "", value: "" }],
    );
    setModalOpen(true);
  };

  const handleDeleteNode = (node) => {
    setDeleteConfirm({ open: true, node, type: "master" });
  };

  const executeDeleteMasterNode = async () => {
    const node = deleteConfirm.node;
    if (!node) return;

    try {
      setActionNodeId(node._id);
      setDeleteConfirm({ open: false, node: null, type: null });
      await axios.delete(
        `${url}/api/master-mappings/delete-item/${masterMapping._id}/${node._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Item deleted successfully");
      await fetchMasterMapping(true);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    } finally {
      setActionNodeId(null);
    }
  };

  const executeDeleteTemplate = async () => {
    try {
      setSaving(true);
      const targetId = masterMapping?._id || projectId;

      if (!targetId) {
        toast.success("Master mapping removed");
        setShowDeleteTemplate(false);
        setSaving(false);
        if (isFirMasterMode) {
          navigate("/settings/manage-fir-mappings");
        } else {
          navigate("/technical/projects/in-progress");
        }
        return;
      }

      let response;
      if (isFirMasterMode) {
        try {
          response = await axios.delete(
            `${url}/api/fir/mappings/${targetId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          response = await axios.delete(
            `${url}/api/master-mappings/delete/${targetId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } else {
        response = await axios.delete(
          `${url}/api/master-mappings/delete/${targetId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response?.data?.success) {
        toast.success("Mapping template deleted successfully");
        setShowDeleteTemplate(false);
        window.dispatchEvent(new Event("refreshMasterMappings"));
        window.dispatchEvent(new Event("refreshFirMappings"));
        setTimeout(() => {
          setSaving(false);
          if (isFirMasterMode) {
            navigate("/settings/manage-fir-mappings");
          } else {
            const remaining = allMasterMappings.filter(
              (m) =>
                m._id !== targetId &&
                m.mappingName?.toLowerCase() !== projectId?.toLowerCase()
            );
            if (remaining.length > 0) {
              navigate(`/technical/mapping/${remaining[0].mappingName.toLowerCase()}`);
            } else {
              navigate("/technical/projects/in-progress");
            }
          }
        }, 1200);
      } else {
        throw new Error(response?.data?.message || "Failed to delete mapping template");
      }
    } catch (error) {
      console.error("Error deleting master mapping:", error);
      toast.error(error.response?.data?.message || "Failed to delete master mapping");
      setSaving(false);
    }
  };

  const handleModalSubmit = async () => {
    if (!nodeTitle.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      setSaving(true);
      if (modalMode === "add") {
        const itemData = {
          title: nodeTitle,
          extraDetails: extraDetailsList.filter((d) => d.key.trim() !== ""),
        };

        if (!masterMapping._id) {
          // If master mapping doesn't exist yet, create it with one component
          const response = await axios.post(
            `${url}/api/master-mappings/add`,
            {
              mappingName: projectId,
              components: [itemData],
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (response.data.success) {
            toast.success("Master mapping created");
            window.dispatchEvent(new Event("refreshMasterMappings"));
            fetchMasterMapping();
          }
        } else {
          // Add to existing
          await axios.post(
            `${url}/api/master-mappings/add-item/${masterMapping._id}`,
            {
              parentId: targetNode?._id || masterMapping._id,
              itemData,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          toast.success("Item added successfully");
          fetchMasterMapping(true);
        }
      } else {
        // Edit mode
        await axios.put(
          `${url}/api/master-mappings/update-item/${masterMapping._id}`,
          {
            itemId: targetNode._id,
            updateData: {
              title: nodeTitle,
              extraDetails: extraDetailsList.filter((d) => d.key.trim() !== ""),
            },
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("Item updated successfully");
        fetchMasterMapping();
      }
      setModalOpen(false);
      localStorage.removeItem(DRAFT_KEY); // Clear draft after successful save
    } catch (error) {
      console.error("Error saving mapping:", error);
      toast.error("Failed to save mapping");
    } finally {
      setSaving(false);
    }
  };

  const handleProjectDetailChange = (id, index, value) => {
    const updateRecursively = (items) => {
      return items.map((item) => {
        if (item._id === id) {
          const newDetails = [...(item.extraDetails || [])];
          if (newDetails[index]) {
            newDetails[index] = { ...newDetails[index], value };
          } else {
            // Fallback for unexpected state
            newDetails[index] = { key: `detail_${index + 1}`, value };
          }
          return { ...item, extraDetails: newDetails };
        }
        if (item.subItems && item.subItems.length > 0) {
          return { ...item, subItems: updateRecursively(item.subItems) };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateRecursively(item.children) };
        }
        return item;
      });
    };
    setProject((prev) => ({
      ...prev,
      components: updateRecursively(prev.components),
    }));
  };

  const generateProjectNode = (mNode) => ({
    _id: Math.random().toString(36).substr(2, 9),
    title: mNode.title,
    extraDetails: (mNode.extraDetails || []).map((d) => ({
      key: d.key,
      value: "",
    })),
    subItems: [],
  });

  const handleProjectAddSibling = (targetId, mNode) => {
    const addRecursively = (items) => {
      let idx = items.findIndex((i) => i._id === targetId);
      if (idx !== -1) {
        const newItems = [...items];
        newItems.splice(idx + 1, 0, generateProjectNode(mNode));
        return newItems;
      }
      return items.map((item) => {
        const key = item.subItems
          ? "subItems"
          : item.children
            ? "children"
            : null;
        if (key && item[key]) {
          return { ...item, [key]: addRecursively(item[key]) };
        }
        return item;
      });
    };
    setProject((prev) => ({
      ...prev,
      components: addRecursively(prev.components),
    }));
  };

  const handleProjectAddChild = (parentId, mNode) => {
    const addRecursively = (items) => {
      return items.map((item) => {
        if (item._id === parentId) {
          const key = item.subItems
            ? "subItems"
            : item.children
              ? "children"
              : "subItems";
          return {
            ...item,
            [key]: [...(item[key] || []), generateProjectNode(mNode)],
          };
        }
        const key = item.subItems
          ? "subItems"
          : item.children
            ? "children"
            : null;
        if (key && item[key]) {
          return { ...item, [key]: addRecursively(item[key]) };
        }
        return item;
      });
    };
    setProject((prev) => ({
      ...prev,
      components: addRecursively(prev.components),
    }));
  };

  const handleProjectDeleteNode = (node) => {
    setDeleteConfirm({ open: true, node, type: "project" });
  };

  const executeDeleteProjectNode = async (id) => {
    try {
      const deleteRecursively = (items) => {
        return items
          .filter((i) => String(i._id) !== String(id))
          .map((item) => {
            const key = item.subItems
              ? "subItems"
              : item.children
                ? "children"
                : null;
            if (key && item[key]) {
              return { ...item, [key]: deleteRecursively(item[key]) };
            }
            return item;
          });
      };
      setProject((prev) => ({
        ...prev,
        components: deleteRecursively(prev.components),
      }));

      await axios.delete(
        `${url}/api/project-mappings/delete-item/${projectId}/${id}`,
        {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        },
      );
      toast.success("Item deleted successfully");
    } catch (error) {
      // If it's a 404, the item might not have been saved to DB yet (still has random ID)
      // or was already deleted. In these cases, the local state update is sufficient.
      if (error.response?.status === 404) {
        toast.success("Item removed");
        return;
      }
      console.error("Error deleting project item:", error);
      toast.error("Failed to delete item from server");
      fetchProjectDetails();
    }
  };

  const handleProjectRemoveDetail = async (
    nodeId,
    detailIdx,
    currentDetails,
  ) => {
    const newDetails = [...(currentDetails || [])];
    newDetails.splice(detailIdx, 1);

    const updateRecursively = (items) => {
      return items.map((item) => {
        if (String(item._id) === String(nodeId)) {
          return { ...item, extraDetails: newDetails };
        }
        const key = item.subItems
          ? "subItems"
          : item.children
            ? "children"
            : null;
        if (key && item[key]) {
          return { ...item, [key]: updateRecursively(item[key]) };
        }
        return item;
      });
    };

    setProject((prev) => ({
      ...prev,
      components: updateRecursively(prev.components),
    }));

    try {
      await axios.put(
        `${url}/api/project-mappings/update-item/${projectId}`,
        {
          itemId: nodeId,
          updateData: { extraDetails: newDetails },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error("Error removing detail:", error);
      fetchProjectDetails();
    }
  };

  const handleProjectSubmit = async () => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${url}/api/project-mappings/add/${projectId}`,
        {
          projectType: project.type || project.projectType,
          components: project.components,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.success) {
        setProject(response.data.data);
        toast.success("Changes saved successfully");
        localStorage.removeItem(DRAFT_KEY); // Clear draft after successful save
      }
      setTimeout(() => {
        setSaving(false);
        navigate("/technical/projects/in-progress");
      }, 1500);
    } catch (error) {
      console.error("Error saving mapping:", error);
      toast.error("Failed to save changes");
      setSaving(false);
    }
  };

  if (loading && !masterMapping && (!project || isMasterMode)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const displayProject = isMasterMode
    ? {
        type:
          projectId.charAt(0).toUpperCase() + projectId.slice(1) + " Project",
        projectName: `Universal ${projectId.charAt(0).toUpperCase() + projectId.slice(1)} Mapping`,
        clientName: "System Master",
        address: "Global Template",
        contactNumber: "N/A",
      }
    : project || projectData;

  const components = isMasterMode
    ? masterMapping?.components || []
    : project?.components || [];

  return (
    <Box sx={{ padding: 1 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ mr: 1, color: COLORS.PRIMARY }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              color: COLORS.SECONDARY,
              fontFamily: FONT_FAMILY,
            }}
          >
            {isMasterMode
              ? (isFirMasterMode ? `FIR Mapping: ${masterMapping?.mappingName || projectId.toUpperCase()}` : `Master Mapping: ${projectId.toUpperCase()}`)
              : "Universal Mapping of Project"}
          </Typography>
          {!isMasterMode && (project?.propertyType || project?.projectType) && (
            <Chip
              label={`FIR Property Type: ${project.propertyType || project.projectType}`}
              size="small"
              sx={{
                ml: 2,
                fontWeight: 700,
                fontSize: "12px",
                backgroundColor: "rgba(66, 26, 110, 0.12)",
                color: COLORS.PRIMARY,
              }}
            />
          )}

          {components.length > 0 && (
            <Tooltip title={isAllExpanded ? "Collapse All" : "Expand All"}>
              <IconButton
                size="small"
                onClick={() => {
                  if (isAllExpanded) {
                    setExpandedNodes(new Set());
                  } else {
                    const allIds = new Set();
                    const traverse = (items) => {
                      items.forEach((item) => {
                        allIds.add(item._id);
                        if (item.subItems) traverse(item.subItems);
                        if (item.children) traverse(item.children);
                      });
                    };
                    traverse(components);
                    setExpandedNodes(allIds);
                  }
                  setIsAllExpanded(!isAllExpanded);
                }}
                sx={{
                  ml: 1,
                  color: COLORS.SECONDARY,
                  backgroundColor: COLORS.HOVER_BACKGROUND,
                  "&:hover": { backgroundColor: COLORS.BORDER },
                  width: 32,
                  height: 32,
                }}
              >
                {isAllExpanded ? (
                  <KeyboardDoubleArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardDoubleArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {(isMasterMode && !hasRole?.("ENGINEER")) ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteTemplate(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                px: 3,
                borderColor: "error.main",
                "&:hover": {
                  borderColor: "error.dark",
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }}
            >
              Delete Template
            </Button>
          ) : (
            !isMasterMode && (
              <Box
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => navigate(`/technical/view-mapping/${projectId}`)}
              >
                <Icon name="sitemap" color={COLORS.SECONDARY} />
              </Box>
            )
          )}
        </Box>
      </Box>

      {/* Content Area - Split 60/40 */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flex: 1,
          minHeight: 0,
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left Section: Tree (60% or 100% depending on details panel presence) */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: { xs: "none", md: !(isMasterMode && hasRole?.("ENGINEER")) ? 0.6 : 1 },
            overflowY: "auto",
            pr: 1,
          }}
        >
          {!isMasterMode && (
            <Box
              sx={{
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: "8px",
                backgroundColor: "#fff",
                padding: 2,
                mb: 4,
                boxShadow: "0px 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  color: COLORS.PRIMARY,
                  mb: 2,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {displayProject.type}
              </Typography>

              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Box>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <b>Project Name:</b> {displayProject.projectName}
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      mt: 1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <b>Developer Name:</b> {displayProject.clientName}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <b>Address:</b> {displayProject.address}
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      mt: 1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <b>Contact:</b> {displayProject.contactNumber}
                  </Typography>
                  {(project?.propertyType || project?.projectType) && (
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.PRIMARY,
                        mt: 1,
                        fontWeight: 700,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      <b>FIR Property Type:</b> {project.propertyType || project.projectType}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Tree Components */}
          <Box
            sx={{
              overflowX: "auto",
              maxWidth: "100%",
              pb: 2,
              "&::-webkit-scrollbar": { height: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: COLORS.LIGHT_SECONDARY,
                borderRadius: "4px",
              },
            }}
          >
            <Box sx={{ minWidth: "max-content" }}>
              {components.map((component, idx, arr) => {
                let mComp = null;
                const relevantMasters = allMasterMappings.filter(m => 
                  (project?.sourceTemplates || []).some(t => t.toLowerCase() === m.mappingName.toLowerCase()) ||
                  (project?.projectType || "").toLowerCase() === m.mappingName.toLowerCase()
                );
                const otherMasters = allMasterMappings.filter(m => !relevantMasters.includes(m));
                const searchList = [...relevantMasters, ...otherMasters];

                for (const m of searchList) {
                  mComp = m.components.find((c) => c.title === component.title);
                  if (mComp) break;
                }

                return (
                  <TreeNode
                    key={component._id || idx}
                    node={component}
                    isTopLevel={true}
                    isFirst={idx === 0}
                    isLast={idx === arr.length - 1}
                    isMaster={isMasterMode}
                    onAdd={handleAddNode}
                    onEdit={handleEditNode}
                    onDelete={
                      isMasterMode ? handleDeleteNode : handleProjectDeleteNode
                    }
                    onCopy={handleCopy}
                    onPaste={handlePaste}
                    hasCopiedNode={!!copiedNode}
                    masterNode={mComp}
                    onDetailChange={handleProjectDetailChange}
                    onAddSibling={handleProjectAddSibling}
                    onAddChild={handleProjectAddChild}
                    onRemoveDetail={handleProjectRemoveDetail}
                    masterMapping={masterMapping}
                    allMasterMappings={allMasterMappings}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                    onInlineRename={handleInlineRename}
                    onMoveUp={(node) => handleMoveNode(node, 'up')}
                    onMoveDown={(node) => handleMoveNode(node, 'down')}
                    actionNodeId={actionNodeId}
                    canEdit={!hasRole?.("ENGINEER")}
                  />
                );
              })}
            </Box>
          </Box>
          {(isMasterMode && !hasRole?.("ENGINEER")) && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleAddNode(null)}
                sx={{
                  mt: 2,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  fontWeight: 600,
                }}
              >
                Add Main Component
              </Button>
              {copiedNode && (
                <Button
                  startIcon={<ContentPasteIcon />}
                  onClick={() => handlePaste(null)}
                  sx={{
                    mt: 2,
                    color: COLORS.SECONDARY,
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                  }}
                >
                  Paste as Main Component
                </Button>
              )}
            </Box>
          )}

          {!isMasterMode && (
            <Button
              variant="contained"
              onClick={handleProjectSubmit}
              disabled={saving}
              sx={{
                mt: 4,
                backgroundColor: COLORS.SECONDARY,
                textTransform: "none",
                padding: "12px 28px",
                borderRadius: "8px",
                fontSize: 16,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                "&:hover": {
                  backgroundColor: COLORS.PRIMARY,
                },
              }}
            >
              {saving ? "Saving..." : "Submit Project Mapping"}
            </Button>
          )}
        </Box>

        {/* Right Section: Sidebar Action Panel (40%) */}
        {!(isMasterMode && hasRole?.("ENGINEER")) && (
          <Box
            sx={{
              flex: { xs: "none", md: 0.4 },
              backgroundColor: "#fff",
              borderRadius: "12px",
              border: `1px solid ${COLORS.BORDER}`,
              height: "fit-content",
              position: "sticky",
              top: 0,
              boxShadow: "0px 4px 12px rgba(0,0,0,0.03)",
              overflow: "hidden",
            }}
          >
            {/* Action Header */}
            <Box
              sx={{
                p: 2,
                backgroundColor: COLORS.PRIMARY,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  p: 1,
                  borderRadius: "8px",
                  display: "flex",
                }}
              >
                {!modalOpen ? (
                  <EditIcon sx={{ fontSize: 20 }} />
                ) : modalMode === "add" ? (
                  <AddIcon sx={{ fontSize: 20 }} />
                ) : (
                  <EditIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
              <Typography sx={{ fontWeight: 600, fontFamily: FONT_FAMILY }}>
                {!modalOpen
                  ? "Details Panel"
                  : modalMode === "add"
                    ? targetNode
                      ? "Add Sub-item"
                      : "Add Main Component"
                    : "Edit Component"}
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Context breadcrumb for sub-items */}
              {modalMode === "add" && targetNode && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: COLORS.LIGHT_SECONDARY + "20",
                    borderRadius: "10px",
                    borderLeft: `4px solid ${COLORS.PRIMARY}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: COLORS.PRIMARY,
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Parent Context
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: COLORS.TEXT,
                      fontSize: "1.1rem",
                    }}
                  >
                    {targetNode.title}
                  </Typography>
                </Box>
              )}

              {!modalOpen ? (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography sx={{ color: COLORS.SECONDARY, opacity: 0.6 }}>
                    Select a component to edit, or click "+" to add a new item
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <TextField
                    fullWidth
                    label="Component Title"
                    variant="outlined"
                    margin="normal"
                    value={nodeTitle}
                    onChange={(e) => setNodeTitle(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "&.Mui-focused fieldset": { borderColor: COLORS.PRIMARY },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: COLORS.PRIMARY,
                      },
                    }}
                  />

                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: COLORS.SECONDARY,
                        mb: 1,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Extra Details
                    </Typography>

                    {extraDetailsList.map((detail, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          size="small"
                          label="Label/Key"
                          placeholder="e.g. Scope"
                          value={detail.key}
                          onChange={(e) => {
                            const newList = [...extraDetailsList];
                            newList[index].key = e.target.value;
                            setExtraDetailsList(newList);
                          }}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Value"
                          placeholder="e.g. Detailed"
                          value={detail.value}
                          onChange={(e) => {
                            const newList = [...extraDetailsList];
                            newList[index].value = e.target.value;
                            setExtraDetailsList(newList);
                          }}
                          sx={{ flex: 1.5 }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const newList = extraDetailsList.filter(
                              (_, i) => i !== index,
                            );
                            setExtraDetailsList(
                              newList.length > 0
                                ? newList
                                : [{ key: "", value: "" }],
                            );
                          }}
                          sx={{ mt: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}

                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() =>
                        setExtraDetailsList([
                          ...extraDetailsList,
                          { key: "", value: "" },
                        ])
                      }
                      sx={{
                        color: COLORS.PRIMARY,
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                    >
                      Add Detail Field
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      mt: 4,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleModalSubmit}
                      disabled={saving}
                      sx={{
                        backgroundColor: COLORS.PRIMARY,
                        py: 1.5,
                        borderRadius: "8px",
                        fontWeight: 700,
                        boxShadow: "0px 4px 10px rgba(94, 46, 145, 0.2)",
                        textTransform: "none",
                        "&:hover": { backgroundColor: COLORS.PRIMARY + "e0" },
                      }}
                    >
                      {saving
                        ? "Processing..."
                        : modalMode === "add"
                          ? "Add Component"
                          : "Update Component"}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => {
                        setModalOpen(false);
                        setModalMode("add");
                        setTargetNode(null);
                        setNodeTitle("");
                        setNodeDetails("");
                        setExtraDetailsList([{ key: "", value: "" }]);
                        // Also clear local storage if user explicitly cancels/clears
                        const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}");
                        delete draft.nodeTitle;
                        delete draft.extraDetailsList;
                        delete draft.modalOpen;
                        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
                      }}
                      sx={{
                        color: COLORS.SECONDARY,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Clear Selection
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${deleteConfirm.node?.title}" and all its sub-items? This action cannot be undone.`}
        onConfirm={() => {
          if (deleteConfirm.type === "master") {
            executeDeleteMasterNode();
          } else {
            executeDeleteProjectNode(deleteConfirm.node?._id);
            setDeleteConfirm({ open: false, node: null, type: null });
          }
        }}
        onCancel={() =>
          setDeleteConfirm({ open: false, node: null, type: null })
        }
      />

      <ConfirmDialog
        open={showDeleteTemplate}
        title="Delete Complete Master Mapping"
        message={`Are you sure you want to delete the entire "${(masterMapping?.propertyType || masterMapping?.mappingName || projectId).toUpperCase()}" master mapping template? This action cannot be undone.`}
        onConfirm={executeDeleteTemplate}
        onCancel={() => setShowDeleteTemplate(false)}
      />
    </Box>
  );
};

export default ProjectMappingPreview;
